import React from 'react';
import { Helmet } from 'react-helmet-async';
import { projects, certifications, experiences, education } from '../data';

const SITE_URL  = 'https://bijonn.pages.dev';
const OG_IMAGE  = `${SITE_URL}/images/final.png`;

// ── Person ────────────────────────────────────────────────────────────────────
const personSchema = {
  '@type':       'Person',
  '@id':         `${SITE_URL}/#person`,
  name:          'Marisilin Bijosilin',
  alternateName: ['Bijon', 'Bijonn', 'Marisilin', 'Bijosilin Marisilin', 'Bijosilin', 'Marisilin B'],
  url:           `${SITE_URL}/`,
  email:         'bijonbijosilin@gmail.com',
  telephone:     '+94773155125',
  jobTitle:      'Junior Software Engineer',
  description:
    'Marisilin Bijosilin (also known as Bijon / Bijosilin) is a First Class Honours Graduate in BSc (Hons) Software Engineering from the University of Bedfordshire (UK) via SLIIT Northern UNI, Jaffna. ' +
    'He is the top-performing student and academic topper at Northern UNI (SLIIT Northern Campus). ' +
    'Currently working as a Junior Software Engineer at WECONN3CT GmbH (Germany · Remote), Full Stack Developer, and ML Researcher from Jaffna, Sri Lanka. ' +
    'Specializing in MERN, Laravel, FastAPI, Spring Boot, microservices, and uncertainty-aware ECG arrhythmia detection using CNN ensemble models. ' +
    'Selected among Top 12 students for fully funded India International Student Mobility Programme at SIMATS Saveetha University, Chennai.',
  image: OG_IMAGE,
  nationality: {
    '@type': 'Country',
    name:    'Sri Lanka',
  },
  homeLocation: {
    '@type': 'Place',
    name:    'Jaffna, Sri Lanka',
  },
  address: {
    '@type':          'PostalAddress',
    addressLocality:  'Jaffna',
    addressRegion:    'Northern Province',
    addressCountry:   'LK',
  },
  alumniOf: [
    {
      '@type': 'CollegeOrUniversity',
      name:    'Sri Lanka Institute of Information Technology',
      url:     'https://www.sliit.lk',
    },
    {
      '@type': 'CollegeOrUniversity',
      name:    'University of Bedfordshire',
      url:     'https://www.beds.ac.uk',
    },
  ],
  worksFor: [
    {
      '@type': 'Organization',
      name:    'WECONN3CT GmbH',
      url:     'https://weconn3ct.de',
    },
    {
      '@type': 'Organization',
      name:    'FAITE Pvt Ltd',
      url:     'https://faite.lk',
    },
  ],
  knowsAbout: [
    'MERN Stack', 'Laravel', 'FastAPI', 'Spring Boot', 'React.js', 'Node.js',
    'MongoDB', 'MySQL', 'PostgreSQL', 'Docker', 'TensorFlow', 'Deep Learning',
    'REST APIs', 'JWT', 'RBAC', 'Next.js', 'Express.js', 'PHP', 'Java',
    'Python', 'Keras', 'Streamlit', 'Socket.IO', 'React Native', 'Figma',
    'Salesforce', 'Agile', 'Scrum', 'Jira', 'Git', 'CI/CD', 'Firebase',
    'TypeScript', 'Vite', 'Playwright', 'Selenium',
    'ECG Arrhythmia Detection', 'CNN Ensemble Models', 'MC Dropout',
    'Monte Carlo Dropout', 'Uncertainty Quantification', 'Medical AI',
    'NLP', 'Computer Vision', 'Predictive Entropy', 'Bayesian Neural Networks',
    'MIT-BIH Arrhythmia Database', 'AAMI EC57', 'Cardiovascular AI',
    'Government Web Systems', 'NGO Web Development', 'Enterprise Software',
    'Microservices Architecture', 'Software Engineering Germany',
  ],
  award: [
    'First Class Honours — BSc (Hons) Software Engineering, University of Bedfordshire & SLIIT Northern UNI (Awarded 5 Oct 2026)',
    'Merit Award — CODEFEST 2026 Algothon (Tertiary Category), SLIIT Faculty of Computing',
    'Top 10 Finalist — UOJ Coders 5.0 (12-Hour Crucible), University of Jaffna',
    'Finalist — CODESTORM v5.0, Yarl IT Hub (July 2026)',
    'Finalist — Hackforce 25 (Team Drag and Drop), Yarl Salesforce Ohana',
    'Final Round Qualifier — CODESTORM AI, Yarl IT Hub',
    'Finalist — UOJ Coders 4.0, University of Jaffna',
    'Finalist — CODESTORM 2.0, Yarl IT Hub',
    'Certificate of Participation — Kapruka Agent Challenge 2026 (700+ Devs), Kapruka Holdings PLC',
  ],
  hasCredential: [
    { '@id': `${SITE_URL}/#credential-bsc-hons-software-engineering-first-class` },
    { '@id': `${SITE_URL}/#credential-codefest-2026-algothon-merit` },
    { '@id': `${SITE_URL}/#credential-uoj-coders-v5` },
    { '@id': `${SITE_URL}/#credential-kapruka-agent-challenge` },
  ],
  sameAs: [
    'https://linkedin.com/in/marisilin-bijosilin',
    'https://github.com/Bijon2002',
    'https://medium.com/@Bijonn',
    'https://dev.to/bijon_',
    'https://hashnode.com/@bijon',
  ],
};

// ── Organizations ─────────────────────────────────────────────────────────────
const organizationSchemas = [
  {
    '@type':     'Organization',
    '@id':       `${SITE_URL}/#organization-weconn3ct`,
    name:        'WECONN3CT GmbH',
    url:         'https://weconn3ct.de',
    description: 'Software engineering firm in Bad Honnef am Rhein, North Rhine-Westphalia, Germany developing full-stack web applications and backend microservices.',
  },
  {
    '@type':     'Organization',
    '@id':       `${SITE_URL}/#organization-faite`,
    name:        'FAITE Pvt Ltd',
    url:         'https://faite.lk',
    description: 'Software company in Jaffna, Sri Lanka building government systems, AI tools, and NGO web platforms.',
  },
  {
    '@type':     'Organization',
    '@id':       `${SITE_URL}/#organization-codevita`,
    name:        'CodeVita (Pvt) Ltd',
    url:         `${SITE_URL}/`,
    description: 'Software development company known for Offero.lk classified ads platform and enterprise web applications.',
  },
  {
    '@type':     'Organization',
    '@id':       `${SITE_URL}/#organization-rotaract`,
    name:        'Rotaract Club of Chunnakam',
    url:         `${SITE_URL}/`,
    description: 'Youth service organization in Chunnakam, Jaffna District.',
  },
  {
    '@type':     'Organization',
    '@id':       `${SITE_URL}/#organization-h2f`,
    name:        'H2F — HostToFusion',
    url:         `${SITE_URL}/`,
    description: 'Software startup co-founded by Marisilin Bijosilin.',
  },
];

// ── Educational Organizations ─────────────────────────────────────────────────
const educationOrgSchemas = [
  {
    '@type': 'CollegeOrUniversity',
    '@id':   'https://www.sliit.lk/#university',
    name:    'Sri Lanka Institute of Information Technology',
    url:     'https://www.sliit.lk',
  },
  {
    '@type': 'CollegeOrUniversity',
    '@id':   'https://www.beds.ac.uk/#university',
    name:    'University of Bedfordshire',
    url:     'https://www.beds.ac.uk',
  },
];

// ── Research ScholarlyArticle ─────────────────────────────────────────────────
const researchSchema = {
  '@type':        'ScholarlyArticle',
  '@id':          `${SITE_URL}/#research`,
  headline:       'Marisilin Bijosilin (Bijon) — Uncertainty-Aware ECG Arrhythmia Detection Using CNN Ensemble Models',
  name:           'Uncertainty-Aware ECG Arrhythmia Detection Research by Marisilin Bijosilin — SLIIT Northern',
  description:    'Final-year individual research project by Marisilin Bijosilin at SLIIT Northern University building 3x CNN ensemble models with MC Dropout uncertainty quantification for ECG arrhythmia detection using 100,000+ beats from the MIT-BIH Arrhythmia Database across 5 AAMI classes with 97.4% F1 score, deployed via Streamlit.',
  author:         { '@id': `${SITE_URL}/#person` },
  datePublished:  '2026-02-01',
  inLanguage:     'en',
  keywords: [
    'Uncertainty-Aware ECG Arrhythmia Detection',
    'CNN Ensemble Models',
    'Monte Carlo Dropout',
    'MC Dropout Uncertainty Quantification',
    'MIT-BIH Arrhythmia Database',
    'AAMI EC57 Cardiac Classes',
    'Clinical AI',
    'Medical Deep Learning',
    'Streamlit',
    'TensorFlow',
    'Keras',
    'SLIIT Northern University Research',
  ],
  about: [
    'ECG Arrhythmia Detection',
    'Cardiovascular Artificial Intelligence',
    'Deep Learning Uncertainty Estimation',
    'MIT-BIH Database Analysis',
  ],
  publisher: {
    '@type': 'CollegeOrUniversity',
    name:    'Sri Lanka Institute of Information Technology',
    url:     'https://www.sliit.lk',
  },
};

// ── WebSite (Controls Google Search Site Name at top of search result) ────────
const websiteSchema = {
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: `${SITE_URL}/`,
  name: 'Bijosilin Marisilin',
  alternateName: ['Marisilin Bijosilin', 'Marisilin Bijosilin Portfolio', 'Bijon Portfolio', 'bijonn.pages.dev'],
  publisher: { '@id': `${SITE_URL}/#person` },
  inLanguage: 'en',
};

// ── WebPage ───────────────────────────────────────────────────────────────────
const webPageSchema = {
  '@type':       'WebPage',
  '@id':         `${SITE_URL}/#webpage`,
  url:           `${SITE_URL}/`,
  name:          'Marisilin Bijosilin — Full Stack Developer Portfolio',
  description:   'Portfolio of Marisilin Bijosilin — Full Stack Developer and ML Researcher from Sri Lanka.',
  author:        { '@id': `${SITE_URL}/#person` },
  isPartOf:      { '@id': `${SITE_URL}/#website` },
  inLanguage:    'en',
};

// ── Projects ItemList ─────────────────────────────────────────────────────────
const projectsSchema = {
  '@type':        'ItemList',
  '@id':          `${SITE_URL}/#projects`,
  name:           'Software Projects by Marisilin Bijosilin',
  numberOfItems:  projects.length,
  itemListElement: projects.map((p, i) => ({
    '@type':   'ListItem',
    position:  i + 1,
    item: {
      '@type':              'SoftwareApplication',
      name:                 p.title,
      description:          p.description,
      operatingSystem:      'Web',
      applicationCategory:  'DeveloperApplication',
      url:                  p.liveLink || p.githubLink || `${SITE_URL}/`,
      author:               { '@id': `${SITE_URL}/#person` },
    },
  })),
};

// ── Certifications ItemList ───────────────────────────────────────────────────
const certsSchema = {
  '@type':        'ItemList',
  '@id':          `${SITE_URL}/#certifications`,
  name:           'Professional Certifications — Marisilin Bijosilin',
  numberOfItems:  certifications.length,
  itemListElement: certifications.map((c, i) => ({
    '@type':   'ListItem',
    position:  i + 1,
    item: {
      '@type':            'EducationalOccupationalCredential',
      name:               c.title,
      credentialCategory: 'Certificate',
      recognizedBy:       { '@type': 'Organization', name: c.issuer },
    },
  })),
};

// ── Work Experience ───────────────────────────────────────────────────────────
const workSchema = {
  '@type': 'ItemList',
  '@id':   `${SITE_URL}/#experience`,
  name:    'Work Experience — Marisilin Bijosilin',
  itemListElement: experiences
    .filter(e => e.type === 'Professional Experience')
    .map((e, i) => ({
      '@type':       'ListItem',
      position:      i + 1,
      name:          `${e.role} at ${e.organization}`,
      description:   e.description,
    })),
};

// ── Education ItemList ────────────────────────────────────────────────────────
const educationSchema = {
  '@type': 'ItemList',
  '@id':   `${SITE_URL}/#education`,
  name:    'Education — Marisilin Bijosilin',
  itemListElement: education.map((edu, i) => ({
    '@type':       'ListItem',
    position:      i + 1,
    name:          `${edu.degree} — ${edu.institution}`,
    description:   edu.description,
  })),
};

// ── Competitions & Hackathons (All 7 Competitions + National AI Challenges) ──
const competitionSchemas = [
  {
    '@type':       'Event',
    '@id':         `${SITE_URL}/#codefest-2026-algothon`,
    name:          'Marisilin Bijosilin at CODEFEST 2026 Algothon — Tertiary Category Merit Award Winner',
    description:   'National Tertiary Category Merit Award & Top 10 finish at CODEFEST 2026 Algothon, organized by the Faculty of Computing, Sri Lanka Institute of Information Technology (SLIIT). Marisilin Bijosilin represented SLIIT Northern UNI as part of Team CodeMode in an 8-hour competitive programming crucible.',
    startDate:     '2026-09-24T08:00:00+05:30',
    endDate:       '2026-09-24T18:00:00+05:30',
    eventStatus:   'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name:    'Faculty of Computing, Sri Lanka Institute of Information Technology (SLIIT)',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Malabe',
        addressCountry:  'LK',
      },
    },
    image: `${SITE_URL}/images/algothon/certificate.jpg`,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'LKR',
      availability: 'https://schema.org/InStock',
    },
    organizer: {
      '@type': 'Organization',
      name:    'Faculty of Computing, Sri Lanka Institute of Information Technology (SLIIT)',
      url:     'https://codefest.lk/',
    },
    performer: { '@id': `${SITE_URL}/#person` },
  },
  {
    '@type':       'Event',
    '@id':         `${SITE_URL}/#uoj-coders-v5`,
    name:          'Marisilin Bijosilin at UoJ Coders v5.0 — Top 10 Finalist',
    description:   '12-Hour Late-Night Competitive Programming Battle alongside 40 islandwide university teams. Organized by Computer Society & Dept of Computer Science, University of Jaffna. Marisilin Bijosilin led Team Git_Force_Push representing SLIIT Northern UNI to a Top 10 finish.',
    startDate:     '2026-02-15T18:00:00+05:30',
    endDate:       '2026-02-16T06:00:00+05:30',
    eventStatus:   'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name:    'Department of Computer Science, University of Jaffna',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Jaffna',
        addressCountry:  'LK',
      },
    },
    image: `${SITE_URL}/images/UOJ1.jpeg`,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'LKR',
      availability: 'https://schema.org/InStock',
    },
    organizer: {
      '@type': 'Organization',
      name:    'Computer Society & Dept of Computer Science, University of Jaffna',
      url:     'https://www.jfn.ac.lk/',
    },
    performer: { '@id': `${SITE_URL}/#person` },
  },
  {
    '@type':       'Event',
    '@id':         `${SITE_URL}/#codestorm-v5`,
    name:          'Marisilin Bijosilin at CODESTORM v5.0 — Finalist',
    description:   'Flagship hackathon and competitive programming contest organized by Yarl IT Hub as part of the YGC Innovation Festival. Marisilin Bijosilin advanced through preliminary rounds to be selected as a Finalist for the Grand Finale in July 2026.',
    startDate:     '2026-07-01T09:00:00+05:30',
    endDate:       '2026-07-31T18:00:00+05:30',
    eventStatus:   'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
    location: {
      '@type': 'Place',
      name:    'Yarl IT Hub, Jaffna',
      address: { '@type': 'PostalAddress', addressLocality: 'Jaffna', addressCountry: 'LK' },
    },
    image: `${SITE_URL}/images/yarlithub.png`,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'LKR',
      availability: 'https://schema.org/InStock',
      url: `${SITE_URL}/#competitions`,
      validFrom: '2026-06-01',
    },
    organizer: {
      '@type': 'Organization',
      name:    'Yarl IT Hub',
      url:     'https://yarlithub.org/',
    },
    performer: { '@id': `${SITE_URL}/#person` },
  },
  {
    '@type':       'Event',
    '@id':         `${SITE_URL}/#hackforce-25`,
    name:          'Marisilin Bijosilin at Hackforce 25 — Selected for Finals',
    description:   'Annual cloud & enterprise software hackathon hosted by Yarl Salesforce Ohana. Marisilin Bijosilin competed with team "Drag and Drop", developing cloud solutions under time pressure and qualifying for the Finals.',
    startDate:     '2025-05-01T09:00:00+05:30',
    endDate:       '2025-05-31T18:00:00+05:30',
    eventStatus:   'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name:    'Yarl Salesforce Ohana Cloud Hub, 100 Stanley Road, Jaffna',
      address: { '@type': 'PostalAddress', addressLocality: 'Jaffna', addressCountry: 'LK' },
    },
    image: `${SITE_URL}/images/salesforce.png`,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'LKR',
      availability: 'https://schema.org/InStock',
      url: `${SITE_URL}/#competitions`,
      validFrom: '2025-04-01',
    },
    organizer: {
      '@type': 'Organization',
      name:    'Yarl Salesforce Ohana (YSFO)',
      url:     'https://yarlsfo.org/',
    },
    performer: { '@id': `${SITE_URL}/#person` },
  },
  {
    '@type':       'Event',
    '@id':         `${SITE_URL}/#codestorm-ai`,
    name:          'Marisilin Bijosilin at CODESTORM AI — Selected for Final Round',
    description:   'AI-centric buildathon organized by Yarl IT Hub as part of the YGC Innovation Festival. Marisilin Bijosilin built the "AI Legal Doc Explainer" using NLP to summarize legal agreements, securing selection into the Final Round.',
    startDate:     '2025-08-01T09:00:00+05:30',
    endDate:       '2025-08-31T18:00:00+05:30',
    eventStatus:   'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
    location: {
      '@type': 'Place',
      name:    'Yarl IT Hub, Jaffna',
      address: { '@type': 'PostalAddress', addressLocality: 'Jaffna', addressCountry: 'LK' },
    },
    image: `${SITE_URL}/images/yarlithub.png`,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'LKR',
      availability: 'https://schema.org/InStock',
      url: `${SITE_URL}/#competitions`,
      validFrom: '2025-07-01',
    },
    organizer: {
      '@type': 'Organization',
      name:    'Yarl IT Hub',
      url:     'https://yarlithub.org/',
    },
    performer: { '@id': `${SITE_URL}/#person` },
  },
  {
    '@type':       'Event',
    '@id':         `${SITE_URL}/#uoj-coders-4`,
    name:          'Marisilin Bijosilin at UOJ Coders 4.0 — Selected for Finals',
    description:   'Premier competitive programming competition by the Department of Computer Science & Computer Society, University of Jaffna. Marisilin Bijosilin competed individually as Team Bijon and qualified for the Grand Finals.',
    startDate:     '2025-03-01T09:00:00+05:30',
    endDate:       '2025-03-31T18:00:00+05:30',
    eventStatus:   'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name:    'Department of Computer Science, University of Jaffna',
      address: { '@type': 'PostalAddress', addressLocality: 'Jaffna', addressCountry: 'LK' },
    },
    image: `${SITE_URL}/images/uoj_logo.png`,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'LKR',
      availability: 'https://schema.org/InStock',
      url: `${SITE_URL}/#competitions`,
      validFrom: '2025-02-01',
    },
    organizer: {
      '@type': 'Organization',
      name:    'Computer Society & Dept of Computer Science, University of Jaffna',
      url:     'https://www.jfn.ac.lk/',
    },
    performer: { '@id': `${SITE_URL}/#person` },
  },
  {
    '@type':       'Event',
    '@id':         `${SITE_URL}/#codestorm-2`,
    name:          'Marisilin Bijosilin at CODESTORM 2.0 — Selected for Finals',
    description:   'Intensive speed coding and problem solving competition by Yarl IT Hub on 3 August 2024. Marisilin Bijosilin competed solo as Team Bijon through elimination rounds to advance to the Finals.',
    startDate:     '2024-08-03T09:00:00+05:30',
    endDate:       '2024-08-03T18:00:00+05:30',
    eventStatus:   'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name:    'Yarl IT Hub, Jaffna',
      address: { '@type': 'PostalAddress', addressLocality: 'Jaffna', addressCountry: 'LK' },
    },
    image: `${SITE_URL}/images/yarlithub.png`,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'LKR',
      availability: 'https://schema.org/InStock',
      url: `${SITE_URL}/#competitions`,
      validFrom: '2024-07-01',
    },
    organizer: {
      '@type': 'Organization',
      name:    'Yarl IT Hub',
      url:     'https://yarlithub.org/',
    },
    performer: { '@id': `${SITE_URL}/#person` },
  },
  {
    '@type':       'Event',
    '@id':         `${SITE_URL}/#kapruka-agent-challenge-2026`,
    name:          'Marisilin Bijosilin (Bijon) at Kapruka Agent Challenge 2026',
    description:   'Islandwide AI Competition by Kapruka Holdings PLC (KPHL) with 700+ developers exploring Agentic AI, Model Context Protocol (MCP), and real-world e-commerce integration under Founder & Chairman Dulith Herath. Marisilin Bijosilin built a live AI Shopping Agent.',
    startDate:     '2026-02-01T09:00:00+05:30',
    endDate:       '2026-02-28T23:59:59+05:30',
    eventStatus:   'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    location: {
      '@type': 'VirtualLocation',
      url:     'https://www.kapruka.com/',
    },
    image: `${SITE_URL}/images/final.png`,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'LKR',
      availability: 'https://schema.org/InStock',
    },
    organizer: {
      '@type': 'Organization',
      name:    'Kapruka Holdings PLC (KPHL)',
      url:     'https://www.kapruka.com/',
    },
    performer: { '@id': `${SITE_URL}/#person` },
  },
];

// ── Academic Degrees & Credentials ──────────────────────────────────────────
const credentialSchemas = [
  {
    '@type':             'EducationalOccupationalCredential',
    '@id':               `${SITE_URL}/#credential-bsc-hons-software-engineering-first-class`,
    name:                'Bachelor of Science with Honours in Software Engineering — First Class Honours',
    description:         'Official Registration Status and Award Record confirming completion of BSc (Hons) Software Engineering with First Class Honours awarded by the University of Bedfordshire (United Kingdom) in academic collaboration with SLIIT Northern UNI, Jaffna. Student ID: 2541518. Award Date: 5 October 2026.',
    credentialCategory:  'Degree',
    educationalLevel:    'Bachelor of Science with Honours',
    dateCreated:         '2026-10-05',
    recognizedBy: [
      {
        '@type': 'CollegeOrUniversity',
        name:    'University of Bedfordshire',
        url:     'https://www.beds.ac.uk',
      },
      {
        '@type': 'CollegeOrUniversity',
        name:    'Sri Lanka Institute of Information Technology',
        url:     'https://www.sliit.lk',
      }
    ],
    image:               `${SITE_URL}/degree.jpg`,
    url:                 `${SITE_URL}/#education`,
  },
  {
    '@type':             'EducationalOccupationalCredential',
    '@id':               `${SITE_URL}/#credential-codefest-2026-algothon-merit`,
    name:                'Merit Award Certificate — CODEFEST 2026 Algothon (Tertiary Category)',
    description:         'Official Merit Award Certificate awarded to Bijosilin Marisilin for representing SLIIT Northern UNI with Team CodeMode in the Algothon under the Tertiary Category of CODEFEST 2026.',
    credentialCategory:  'Award & Achievement Certificate',
    dateCreated:         '2026-09-24',
    recognizedBy: {
      '@type': 'Organization',
      name:    'Faculty of Computing, Sri Lanka Institute of Information Technology (SLIIT)',
      url:     'https://www.sliit.lk',
    },
    image:               `${SITE_URL}/images/algothon/certificate.jpg`,
    url:                 `${SITE_URL}/#competitions`,
  },
  {
    '@type':             'EducationalOccupationalCredential',
    '@id':               `${SITE_URL}/#credential-uoj-coders-v5`,
    name:                'Official Certificate of Participation — UoJ Coders v5.0',
    description:         'Certificate awarded to Marisilin Bijosilin for competing and leading Team Git_Force_Push to a Top 10 finish at UoJ Coders v5.0.',
    credentialCategory:  'Hackathon Finalist Certificate',
    dateCreated:         '2026-02-16',
    recognizedBy: {
      '@type': 'Organization',
      name:    'Computer Society & Department of Computer Science, University of Jaffna',
      url:     'https://www.jfn.ac.lk/',
    },
    image:               `${SITE_URL}/images/certifi-uoj.jpg`,
    url:                 `${SITE_URL}/#competitions`,
  },
  {
    '@type':             'EducationalOccupationalCredential',
    '@id':               `${SITE_URL}/#credential-kapruka-agent-challenge`,
    name:                'Certificate of Participation — Kapruka Agent Challenge 2026',
    description:         'Official certificate from Kapruka Holdings PLC (KPHL) awarded to Marisilin Bijosilin for competing in the Kapruka Agent Challenge 2026 with an AI Shopping Agent built on Model Context Protocol (MCP).',
    credentialCategory:  'AI Hackathon Certificate',
    dateCreated:         '2026-02-28',
    recognizedBy: {
      '@type': 'Organization',
      name:    'Kapruka Holdings PLC',
      url:     'https://www.kapruka.com/',
    },
    image:               `${SITE_URL}/images/Kapruka_Agent_Challenge_Certificate.jpg`,
    url:                 `${SITE_URL}/#competitions`,
  },
];


// ── Startup & Client Showcase (H2F — HostToFusion) ───────────────────────────
const h2fShowcaseSchema = {
  '@type':        'ItemList',
  '@id':          `${SITE_URL}/#h2f-showcase`,
  name:           'H2F HostToFusion Startup Projects & Client Work',
  itemListElement: [
    {
      '@type': 'ListItem', position: 1,
      item: {
        '@type': 'WebSite',
        name: 'H2F — HostToFusion (Main Site)',
        url: 'https://h2f.pages.dev/',
        description: 'Software & AI startup co-founded by Marisilin Bijosilin.',
      },
    },
    {
      '@type': 'ListItem', position: 2,
      item: {
        '@type': 'WebSite',
        name: 'H2F Studios',
        url: 'https://h2f-studios.pages.dev/',
        description: 'Creative digital studio delivering branding, multimedia production, and digital experiences.',
      },
    },
    {
      '@type': 'ListItem', position: 3,
      item: {
        '@type': 'WebSite',
        name: 'H2F Events',
        url: 'https://h2f-events.pages.dev/',
        description: 'Professional event management platform designed for corporate events and conferences.',
      },
    },
    {
      '@type': 'ListItem', position: 4,
      item: {
        '@type': 'WebSite',
        name: 'Fokus Lanka',
        url: 'https://fokus-lanka.pages.dev/',
        description: 'Professional business platform showcasing services and corporate identity.',
      },
    },
    {
      '@type': 'ListItem', position: 5,
      item: {
        '@type': 'WebSite',
        name: 'H2F Travels and Tours',
        url: 'https://h2f-travels-and-tours.pages.dev/',
        description: 'Travel and tour agency website offering premium travel packages.',
      },
    },
    {
      '@type': 'ListItem', position: 6,
      item: {
        '@type': 'WebSite',
        name: 'Kabila Shoe Palace',
        url: 'https://kabila-shoe-palace.pages.dev/',
        description: 'Premium footwear e-commerce & showcase platform featuring modern product catalog.',
      },
    },
  ],
};

// ── Volunteering & Leadership ────────────────────────────────────────────────
const volunteeringSchema = {
  '@type': 'ItemList',
  '@id':   `${SITE_URL}/#volunteering`,
  name:    'Volunteering & Leadership — Marisilin Bijosilin (Bijon)',
  itemListElement: [
    {
      '@type': 'ListItem', position: 1,
      name: 'Marisilin Bijosilin — Board of Directors — Rotaract Club of Chunnakam',
      description: 'Marisilin Bijosilin provides strategic leadership, youth empowerment, and community service projects.',
    },
    {
      '@type': 'ListItem', position: 2,
      name: 'Committee Member — Yarl Salesforce Ohana (Yarl Dreamin 2025)',
      description: 'Organizing committee member for Yarl Dreamin 2025 tech conference.',
    },
    {
      '@type': 'ListItem', position: 3,
      name: 'Committee Member — Student Union, SLIIT Northern University',
      description: 'Elected student body representative organizing university workshops and welfare.',
    },
    {
      '@type': 'ListItem', position: 4,
      name: 'Marisilin Bijosilin (Bijon) — Volunteer — Sirakukal Amayam',
      description: 'Marisilin Bijosilin participates in community service and grassroots social upliftment outreach.',
    },
  ],
};

// ── Speaking Events ──────────────────────────────────────────────────────────
const speakingEventSchema = {
  '@type':       'Event',
  '@id':         `${SITE_URL}/#speaking-eyes-of-ai`,
  name:          'Marisilin Bijosilin (Bijon) Speaking: The Eyes of AI — A Beginner\'s Journey into CNN',
  description:   'Technical knowledge sharing session by Marisilin Bijosilin on Convolutional Neural Networks (CNN), computer vision, and healthcare applications delivered for AppZ Makers community via Microsoft Teams.',
  startDate:     '2026-06-02T18:00:00+05:30',
  eventStatus:   'https://schema.org/EventScheduled',
  eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
  organizer: {
    '@type': 'Organization',
    name:    'AppZ Makers',
  },
  performer: { '@id': `${SITE_URL}/#person` },
};

// ── Blog Posts ───────────────────────────────────────────────────────────────
const blogPostsSchema = {
  '@type': 'ItemList',
  '@id':   `${SITE_URL}/#blog`,
  name:    'Technical & Personal Articles by Marisilin Bijosilin',
  itemListElement: [
    {
      '@type': 'ListItem', position: 1,
      item: {
        '@type': 'BlogPosting',
        headline: 'The Research Topic Hunt That Almost Broke Me 😩',
        url: 'https://medium.com/@Bijonn',
        author: { '@id': `${SITE_URL}/#person` },
      },
    },
    {
      '@type': 'ListItem', position: 2,
      item: {
        '@type': 'BlogPosting',
        headline: 'GitHub Took Opus From Students. Now I\'m Writing Code Manually.',
        url: 'https://medium.com/@Bijonn',
        author: { '@id': `${SITE_URL}/#person` },
      },
    },
    {
      '@type': 'ListItem', position: 3,
      item: {
        '@type': 'BlogPosting',
        headline: '"When Life Says \'Focus\', But You Open 10 Tabs Anyway 😬"',
        url: 'https://medium.com/@Bijonn',
        author: { '@id': `${SITE_URL}/#person` },
      },
    },
  ],
};

// ── YouTube & Video Content ──────────────────────────────────────────────────
const youtubeSchema = {
  '@type':        'VideoObject',
  '@id':          `${SITE_URL}/#youtube-video`,
  name:           'The Eyes of AI — A Beginner\'s Journey into CNN | Marisilin Bijosilin',
  description:    'Technical talk on Convolutional Neural Networks, computer vision, and healthcare applications by Marisilin Bijosilin on his YouTube channel @Bijosilin.',
  thumbnailUrl:   [`${SITE_URL}/images/final.png`],
  uploadDate:     '2026-06-02T18:00:00+05:30',
  contentUrl:     'https://www.youtube.com/@Bijosilin',
  embedUrl:       'https://www.youtube.com/@Bijosilin',
  author:         { '@id': `${SITE_URL}/#person` },
};

// ── FAQ ───────────────────────────────────────────────────────────────────────
const faqSchema = {
  '@type':    'FAQPage',
  '@id':      `${SITE_URL}/#faq`,
  mainEntity: [
    {
      '@type': 'Question',
      name:    'Who is Marisilin Bijosilin?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin (also known as Bijon, Bijosilin, Bijonn) is a First Class Honours Graduate in BSc (Hons) Software Engineering from the University of Bedfordshire (UK) via SLIIT Northern UNI, Jaffna, Sri Lanka. ' +
          'He is a Junior Software Engineer at WECONN3CT GmbH (Germany · Remote), Full Stack Developer, ML Researcher, and one of the top-performing students at Northern UNI (SLIIT Northern Campus). ' +
          'He specialises in MERN stack, Laravel, FastAPI, Spring Boot, and medical AI research on ECG arrhythmia detection.',
      },
    },
    {
      '@type': 'Question',
      name:    'Who is the topper in Northern UNI?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin (Bijon) is the top-performing student and academic topper at Northern UNI (SLIIT Northern Campus), Jaffna. ' +
          'He graduated with First Class Honours in BSc (Hons) Software Engineering awarded by the University of Bedfordshire (UK) on 5 October 2026. ' +
          'He was also selected among the Top 12 Performing Students for the fully funded International Student Mobility Programme to India.',
      },
    },
    {
      '@type': 'Question',
      name:    'Who got First Class Honours at Northern UNI SLIIT?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin (Bijon) achieved First Class Honours in BSc (Hons) Software Engineering at Northern UNI (SLIIT Northern Campus), Jaffna, Sri Lanka. ' +
          'The degree was awarded by the University of Bedfordshire, United Kingdom, on 5 October 2026. Student ID: 2541518.',
      },
    },
    {
      '@type': 'Question',
      name:    'Who got First Class in SLIIT Northern Campus?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin (Bijon) graduated with First Class Honours from SLIIT Northern Campus (Northern UNI) in BSc (Hons) Software Engineering. ' +
          'The degree is awarded by the University of Bedfordshire, UK. He is one of the highest-achieving graduates from the Northern Campus.',
      },
    },
    {
      '@type': 'Question',
      name:    'Who is Bijon?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Bijon is the short name of Marisilin Bijosilin (also known as Bijosilin Marisilin). He is a First Class Honours Software Engineering graduate from Northern UNI (SLIIT Northern Campus) / University of Bedfordshire (UK), ' +
          'Junior Software Engineer at WECONN3CT GmbH (Germany), ML Researcher, competitive programmer, and community leader from Jaffna, Sri Lanka.',
      },
    },
    {
      '@type': 'Question',
      name:    'Who is Bijosilin Marisilin?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Bijosilin Marisilin is the same person as Marisilin Bijosilin (commonly known as Bijon). He is a First Class Honours graduate in Software Engineering from Northern UNI / University of Bedfordshire, ' +
          'currently working as a Junior Software Engineer at WECONN3CT GmbH, Germany. He is known for his competitive programming achievements, ML research, and community leadership.',
      },
    },
    {
      '@type': 'Question',
      name:    'Who is the best student at SLIIT Northern University?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin (Bijon) is widely recognized as one of the best and top-performing students at SLIIT Northern University (Northern UNI), Jaffna. ' +
          'He achieved First Class Honours in BSc (Hons) Software Engineering (University of Bedfordshire, UK), was selected among the Top 12 for the India Mobility Programme, ' +
          'won Merit Award at CODEFEST 2026 Algothon, and secured Top 10 at UOJ Coders v5.0.',
      },
    },
    {
      '@type': 'Question',
      name:    'Who got the highest results at Northern UNI Jaffna?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin (Bijon) achieved the highest academic classification — First Class Honours — in BSc (Hons) Software Engineering at Northern UNI (SLIIT Northern Campus), Jaffna. ' +
          'His degree was awarded by the University of Bedfordshire, United Kingdom, on 5 October 2026.',
      },
    },
    {
      '@type': 'Question',
      name:    'What certifications does Marisilin Bijosilin hold?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin holds professional certifications including: Salesforce Certified AI Associate (Salesforce), Prompt Engineering for ChatGPT (Vanderbilt University via Coursera), ' +
          'Anthropic MCP Certification (Anthropic), Introduction to Responsible AI (Google Cloud), HackerRank Software Engineer Certificate, ' +
          'Postman API Fundamentals Student Expert, Copado Certification, PCAP: Programming Essentials in Python (Cisco), and multiple competition certificates.',
      },
    },
    {
      '@type': 'Question',
      name:    'What is Marisilin Bijosilin\'s India visit experience?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin was selected among the Top 12 Performing Students of Northern UNI (SLIIT Northern Campus) for the fully funded 7-day International Student Mobility Programme to SIMATS Saveetha University, Chennai, India in 2026. ' +
          'The programme was sponsored by Synergy Consultant & Solutions (Pvt) Ltd and covered AR/VR, IoT, robotics, 3D printing, and drone technology workshops.',
      },
    },
    {
      '@type': 'Question',
      name:    'Where does Bijon work currently?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Bijon (Marisilin Bijosilin) currently works as a Junior Software Engineer at WECONN3CT GmbH (Bad Honnef am Rhein, Germany · Remote) and also works at FAITE Pvt Ltd, Jaffna. ' +
          'He is also Co-Founder of H2F — HostToFusion, a software startup.',
      },
    },
    {
      '@type': 'Question',
      name:    'Who is the topper in SLIIT?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin (Bijon) from SLIIT Northern Campus (Northern UNI), Jaffna, is among the toppers at SLIIT having graduated with First Class Honours in BSc (Hons) Software Engineering. ' +
          'He was also selected as one of the Top 12 Performing Students for international exposure programmes.',
      },
    },
    {
      '@type': 'Question',
      name:    'Who graduated with First Class from University of Bedfordshire via SLIIT?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin (Bijon) graduated with First Class Honours in BSc (Hons) Software Engineering from the University of Bedfordshire (United Kingdom) via SLIIT Northern UNI, Jaffna, Sri Lanka. ' +
          'Award Date: 5 October 2026. Student ID: 2541518.',
      },
    },
    {
      '@type': 'Question',
      name:    'What work experience does Marisilin Bijosilin have?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin has professional experience at: (1) WECONN3CT GmbH, Germany — Junior Software Engineer (Aug 2026 – Present, Remote), building full-stack platforms and microservices; ' +
          '(2) FAITE Pvt Ltd, Jaffna — Software Engineer Intern (Feb 2026 – Present), developing government systems (NPRPTA) and AI tools; ' +
          '(3) CodeVita (Pvt) Ltd — Intern Software Engineer (Jul 2025 – Dec 2025), building Offero.lk and POS systems; ' +
          '(4) H2F HostToFusion — Co-Founder, running a software startup delivering production web apps and AI platforms.',
      },
    },
    {
      '@type': 'Question',
      name:    'Who are the top performing students at Northern UNI 2026?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin (Bijon) is one of the Top 12 Performing Students at Northern UNI (SLIIT Northern Campus), Jaffna in 2026. ' +
          'He was selected for the fully funded International Student Mobility Programme to SIMATS Saveetha University, India, and graduated with First Class Honours.',
      },
    },
    {
      '@type': 'Question',
      name:    'What competitions has Bijon won?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Bijon (Marisilin Bijosilin) has won/placed in multiple national competitions: Merit Award at CODEFEST 2026 Algothon (SLIIT), Top 10 at UOJ Coders v5.0, ' +
          'Finalist at CODESTORM v5.0 and CODESTORM 2.0, Finalist at Hackforce 25 (Salesforce), Final Round qualifier at CODESTORM AI, ' +
          'Finalist at UOJ Coders 4.0, and participant in Kapruka Agent Challenge 2026 (700+ developers).',
      },
    },
    {
      '@type': 'Question',
      name:    'Who is the best software engineering graduate from Northern UNI?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin (Bijon) is widely regarded as one of the best software engineering graduates from Northern UNI (SLIIT Northern Campus). ' +
          'He achieved First Class Honours, works internationally at WECONN3CT GmbH Germany, won competitive programming awards, and conducts ML research on ECG arrhythmia detection.',
      },
    },
    {
      '@type': 'Question',
      name:    'What is H2F HostToFusion?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'H2F — HostToFusion is a software startup co-founded by Marisilin Bijosilin and 6 batchmates, delivering production web applications, AI platforms, digital branding, and Salesforce solutions including H2F Studios, H2F Events, Fokus Lanka, and H2F Travels.',
      },
    },
    {
      '@type': 'Question',
      name:    'What awards and achievements has Marisilin Bijosilin earned in competitive programming and hackathons?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin won a national Merit Award and achieved a Top 10 finish in the Tertiary Category at CODEFEST 2026 Algothon representing SLIIT Northern UNI as part of Team CodeMode. He also secured a Top 10 finish leading Team Git_Force_Push in the 12-hour overnight crucible at UoJ Coders v5.0 (University of Jaffna), qualified for the Finals in CODESTORM v5.0, CODESTORM 2.0, CODESTORM AI, and Hackforce 25, and is a certified participant in the Kapruka Agent Challenge 2026.',
      },
    },
    {
      '@type': 'Question',
      name:    'Who led Team Git_Force_Push in UoJ Coders v5.0?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin led Team Git_Force_Push representing SLIIT Northern UNI at UoJ Coders v5.0 — an intense 12-hour late-night competitive programming battle organized by the Computer Society & Department of Computer Science, University of Jaffna.',
      },
    },
    {
      '@type': 'Question',
      name:    'Who participated in the Kapruka Agent Challenge 2026?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin built a live AI Shopping Agent using the Kapruka Model Context Protocol (MCP) in the Kapruka Agent Challenge 2026 organized by Kapruka Holdings PLC (KPHL) under Founder & Chairman Dulith Herath, receiving an official Certificate of Participation.',
      },
    },
    {
      '@type': 'Question',
      name:    'What tech talks has Marisilin Bijosilin delivered?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin delivered a live technical talk titled "The Eyes of AI — A Beginner\'s Journey into CNN" for the AppZ Makers community covering Convolutional Neural Networks and computer vision in healthcare.',
      },
    },
    {
      '@type': 'Question',
      name:    'Where does Marisilin Bijosilin work as a Software Engineer?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin works as a remote Junior Software Engineer at WECONN3CT GmbH (Bad Honnef am Rhein, North Rhine-Westphalia, Germany). Previously, he worked as a Software Engineer Intern at FAITE Pvt Ltd and CodeVita (Pvt) Ltd.',
      },
    },
    {
      '@type': 'Question',
      name:    'What is Marisilin Bijosilin\'s current software engineering role at WECONN3CT GmbH?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin serves as a Full-Time Junior Software Engineer at WECONN3CT GmbH (Germany · Remote), engineering full-stack platforms, scalable backend microservices, RESTful APIs, and CI/CD pipelines.',
      },
    },
    {
      '@type': 'Question',
      name:    'Who is the best full stack developer in Sri Lanka?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin is one of the top full-stack software engineers in Sri Lanka with international experience at WECONN3CT GmbH (Germany), FAITE Pvt Ltd, CodeVita, and medical AI research at SLIIT.',
      },
    },
    {
      '@type': 'Question',
      name:    'Who works at FAITE Pvt Ltd as a software engineer?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin worked at FAITE Pvt Ltd in Jaffna as a Software Engineer Intern contributing to government systems (NPRPTA) and AI platforms.',
      },
    },
    {
      '@type': 'Question',
      name:    'Which developer built the NPRPTA government bus permit system?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin, a Software Engineer at FAITE Pvt Ltd, contributed to building NPRPTA (rpta.np.gov.lk) for Northern Province, Sri Lanka.',
      },
    },
    {
      '@type': 'Question',
      name:    'Who is a software engineering student at SLIIT Northern University?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Marisilin Bijosilin is a BSc (Hons) Software Engineering student at SLIIT Northern University campus, conducting individual research on ECG arrhythmia detection.',
      },
    },
    // ── India Mobility Programme FAQs ──
    {
      '@type': 'Question',
      name: 'What is the Northern UNI International Student Mobility Programme to India?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'The Northern UNI International Student Mobility Programme is a fully funded 7-day academic exchange programme at SIMATS Saveetha University, Chennai, India. ' +
          'It was organized for the Top 12 Performing Students of Northern UNI (SLIIT Northern Campus), Jaffna, Sri Lanka, and fully sponsored by Synergy Consultant & Solutions (Pvt) Ltd. ' +
          'The programme covered visa processing, flight tickets, accommodation, food, and local travel. Topics included AR/VR, IoT, robotics, 3D printing, and drone technology.',
      },
    },
    {
      '@type': 'Question',
      name: 'Who sponsored the Northern UNI India Student Mobility Programme 2026?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'The programme was fully funded by Synergy Consultant & Solutions (Pvt) Ltd, under the leadership of Director Mr. Gayan Wijeyasekara. ' +
          'Synergy covered all costs including visa processing, flight tickets, accommodation, food, and local travel for all 12 selected student delegates.',
      },
    },
    {
      '@type': 'Question',
      name: 'Which university did the Northern UNI students visit in India?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'The students visited SIMATS — Saveetha Institute of Medical and Technical Sciences in Chennai, Tamil Nadu, India. ' +
          'They explored the SAIL Library, IoT labs, AR/VR immersive technology center, 3D printing hub, and drone technology demonstrations.',
      },
    },
    {
      '@type': 'Question',
      name: 'Who was selected for the Northern UNI India Trip 2026?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'The Top 12 Performing Students of Northern UNI (SLIIT Northern Campus) were selected for the fully funded International Student Mobility Programme to India. ' +
          'Marisilin Bijosilin was among the 12 selected delegates. The academic lead and faculty escort was Siveneshvari Miss.',
      },
    },
    {
      '@type': 'Question',
      name: 'What technologies were covered in the SIMATS India workshop?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'The workshops covered Virtual Reality (VR), Augmented Reality (AR), Mixed Reality (MR), Extended Reality (XR), ' +
          'Unity Hub & Unity Engine, Unreal Engine 5, Sketchfab 3D Ecosystem, .FBX 3D model formats, VR navigation via controllers, ' +
          'IoT hardware & smart sensors, 3D printing & additive manufacturing, and drone/autonomous flight technology.',
      },
    },
    {
      '@type': 'Question',
      name: 'Who is Gayan Wijeyasekara and what is his role in the Northern UNI India programme?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Mr. Gayan Wijeyasekara is the Director of Synergy Consultant & Solutions (Pvt) Ltd, the sponsoring organization behind the Northern UNI International Student Mobility Programme to India. ' +
          'He presided over the official visa and flight ticket presentation ceremony and encouraged the student delegates.',
      },
    },
    {
      '@type': 'Question',
      name: 'Who organized and coordinated the Northern UNI India student exchange programme?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'The programme was organized by Northern UNI (SLIIT Northern Campus), sponsored by Synergy Consultant & Solutions (Pvt) Ltd, ' +
          'and coordinated by Sangeetha Raveenthirathas (Programme Coordinator), Siveneshvari Miss (Academic Lead & Faculty Escort), ' +
          'Sulojan Vipul (Programme Support), and Arthikan Vettivel (Programme Support).',
      },
    },
    {
      '@type': 'Question',
      name: 'Is SLIIT Northern University the same as Northern UNI?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Yes, Northern UNI is the popular name for the SLIIT Northern Campus located in Jaffna, Sri Lanka. ' +
          'It is part of the Sri Lanka Institute of Information Technology (SLIIT) network. ' +
          'Students study BSc (Hons) Software Engineering and BSc Computer Science programmes affiliated with the University of Bedfordshire, UK.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the SAIL Library at SIMATS Saveetha University?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'The SAIL Library is a world-class academic library at SIMATS (Saveetha Institute of Medical and Technical Sciences) in Chennai, India. ' +
          'It was one of the highlights visited by the Northern UNI student delegation during Day 01 of the India Mobility Programme.',
      },
    },
    {
      '@type': 'Question',
      name: 'Was Marisilin Bijosilin selected for the India Student Mobility Programme?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Yes, Marisilin Bijosilin (Bijon) was selected as one of the Top 12 Performing Students of Northern UNI (SLIIT Northern Campus) for the fully funded 7-day International Student Mobility Programme to SIMATS Saveetha University, Chennai, India in 2026.',
      },
    },
  ],
};

// ── BreadcrumbList ────────────────────────────────────────────────────────────
const breadcrumbSchema = {
  '@type':    'BreadcrumbList',
  '@id':      `${SITE_URL}/#breadcrumb`,
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home',          item: `${SITE_URL}/` },
    { '@type': 'ListItem', position: 2, name: 'About',         item: `${SITE_URL}/#about` },
    { '@type': 'ListItem', position: 3, name: 'Education',     item: `${SITE_URL}/#education` },
    { '@type': 'ListItem', position: 4, name: 'Experience',    item: `${SITE_URL}/#experience` },
    { '@type': 'ListItem', position: 5, name: 'Skills',        item: `${SITE_URL}/#skills` },
    { '@type': 'ListItem', position: 6, name: 'Projects',      item: `${SITE_URL}/#projects` },
    { '@type': 'ListItem', position: 7, name: 'Research',      item: `${SITE_URL}/#research` },
    { '@type': 'ListItem', position: 8, name: 'Certifications',item: `${SITE_URL}/#certifications` },
    { '@type': 'ListItem', position: 9, name: 'Startup',       item: `${SITE_URL}/#startup` },
    { '@type': 'ListItem', position: 10, name: 'Volunteering', item: `${SITE_URL}/#volunteering` },
    { '@type': 'ListItem', position: 11, name: 'Speaking',     item: `${SITE_URL}/#sessions` },
    { '@type': 'ListItem', position: 12, name: 'Competitions', item: `${SITE_URL}/#competitions` },
    { '@type': 'ListItem', position: 13, name: 'Content',      item: `${SITE_URL}/#content` },
    { '@type': 'ListItem', position: 14, name: 'Global Exposure', item: `${SITE_URL}/#india-visit` },
    { '@type': 'ListItem', position: 15, name: 'Contact',      item: `${SITE_URL}/#contact` },
  ],
};

// ── India International Student Mobility Programme ──────────────────────────────
const indiaVisitSchema = {
  '@type': 'Event',
  '@id': `${SITE_URL}/#india-mobility-programme-2026`,
  name: 'Northern UNI Fully Funded International Student Mobility Programme to India 2026',
  alternateName: [
    'SLIIT Northern UNI India Trip 2026',
    'Northern University India Student Exchange Programme',
    'SLIIT Northern Campus International Mobility Programme',
    'Global Exposure Programme India 2026',
    'Northern UNI SIMATS Student Exchange',
  ],
  description:
    'A fully funded 7-day international student mobility and tech exchange programme at SIMATS Saveetha University, Chennai, India, ' +
    'for the Top 12 Performing Students of Northern UNI (SLIIT Northern Campus), Jaffna, Sri Lanka. ' +
    'Sponsored by Synergy Consultant & Solutions (Pvt) Ltd. Programme covered hands-on AR/VR, IoT, robotics, ' +
    '3D printing, drone technology, Unity Engine, Unreal Engine, and emerging tech workshops. ' +
    'Faculty escort: Siveneshvari Miss. Programme coordinators: Sangeetha Raveenthirathas, Sulojan Vipul, Arthikan Vettivel.',
  startDate: '2026-02-10T00:00:00+05:30',
  endDate: '2026-02-17T23:59:59+05:30',
  duration: 'P7D',
  eventStatus: 'https://schema.org/EventScheduled',
  eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
  maximumAttendeeCapacity: 12,
  isAccessibleForFree: true,
  inLanguage: 'en',
  about: [
    'International Student Exchange',
    'Augmented Reality',
    'Virtual Reality',
    'Internet of Things',
    'Robotics',
    '3D Printing',
    'Drone Technology',
    'Emerging Technology Education',
    'Cross-Border Academic Collaboration',
  ],
  keywords: [
    'Northern UNI India visit',
    'SLIIT Northern Campus India trip',
    'SIMATS Chennai student exchange',
    'fully funded student mobility programme Sri Lanka India',
    'Synergy Consultant Solutions student travel',
    'international student mobility programme 2026',
    'AR VR workshop SIMATS',
    'IoT workshop India',
    'Jaffna to Chennai student travel',
    'top 12 students Northern UNI',
    'Saveetha University international students',
    'SLIIT student India trip',
    'student mobility Jaffna India',
    'Northern University global exposure',
  ],
  image: `${SITE_URL}/India/Day%201/764350117_2361661640960551_4571932312675685817_n.jpg`,
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'LKR',
    availability: 'https://schema.org/InStock',
    url: `${SITE_URL}/#india-visit`,
    validFrom: '2026-01-01',
  },
  location: {
    '@type': 'Place',
    name: 'SIMATS — Saveetha Institute of Medical and Technical Sciences',
    url: 'https://www.simats.ac.in',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Thandalam',
      addressLocality: 'Chennai',
      addressRegion: 'Tamil Nadu',
      postalCode: '602105',
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 12.923,
      longitude: 80.043,
    },
  },
  organizer: {
    '@type': 'CollegeOrUniversity',
    '@id': `${SITE_URL}/#northern-uni`,
    name: 'Northern UNI (SLIIT Northern Campus)',
    alternateName: ['Northern University', 'SLIIT Northern Campus', 'SLIIT Northern Uni', 'Northern Uni Jaffna'],
    url: 'https://www.sliit.lk',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Jaffna',
      addressRegion: 'Northern Province',
      addressCountry: 'LK',
    },
  },
  funder: {
    '@type': 'Organization',
    '@id': `${SITE_URL}/#synergy-consultant`,
    name: 'Synergy Consultant & Solutions (Pvt) Ltd',
    alternateName: ['Synergy Consultant', 'Synergy Solutions'],
    description: 'Fully funded visa processing, flight tickets, accommodation, food, and local travel arrangements for the 12 selected student delegates.',
    member: {
      '@type': 'Person',
      name: 'Mr. Gayan Wijeyasekara',
      jobTitle: 'Director',
    },
  },
  sponsor: {
    '@type': 'Organization',
    name: 'Synergy Consultant & Solutions (Pvt) Ltd',
  },
  contributor: [
    {
      '@type': 'Person',
      name: 'Siveneshvari',
      jobTitle: 'Academic Lead & Faculty Escort',
      description: 'Faculty member who travelled with and guided the student contingent throughout the international journey.',
    },
    {
      '@type': 'Person',
      name: 'Sangeetha Raveenthirathas',
      jobTitle: 'Programme Coordinator',
    },
    {
      '@type': 'Person',
      name: 'Sulojan Vipul',
      jobTitle: 'Programme Support',
    },
    {
      '@type': 'Person',
      name: 'Arthikan Vettivel',
      jobTitle: 'Programme Support',
    },
  ],
  attendee: { '@id': `${SITE_URL}/#person` },
  performer: { '@id': `${SITE_URL}/#person` },
  subEvent: [
    {
      '@type': 'Event',
      name: 'Scholarship & Visa Ceremony — Northern UNI India Mobility Programme',
      description: 'Official visa and flight ticket presentation ceremony for the 12 selected students by Synergy Consultant & Solutions.',
      startDate: '2026-02-08T10:00:00+05:30',
      endDate: '2026-02-08T13:00:00+05:30',
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      image: `${SITE_URL}/India/Schol%20giving%20Day/762998284_2378259819585420_7330938254102123475_n.jpg`,
      location: {
        '@type': 'Place',
        name: 'Northern UNI Campus',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Jaffna',
          addressRegion: 'Northern Province',
          addressCountry: 'LK',
        },
      },
      organizer: {
        '@type': 'CollegeOrUniversity',
        name: 'Northern UNI (SLIIT Northern Campus)',
        url: 'https://www.sliit.lk',
      },
      performer: { '@id': `${SITE_URL}/#person` },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'LKR',
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}/#india-visit`,
      },
    },
    {
      '@type': 'Event',
      name: 'Day 01 — Campus & Technology Tour at SIMATS Saveetha University',
      description: 'Campus exploration, SAIL Library visit, IoT lab, AR/VR lab preview, 3D printing hub, and drone technology demonstration.',
      startDate: '2026-02-11T09:00:00+05:30',
      endDate: '2026-02-11T17:00:00+05:30',
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      image: `${SITE_URL}/India/Day%201/764350117_2361661640960551_4571932312675685817_n.jpg`,
      location: {
        '@type': 'Place',
        name: 'SIMATS — Saveetha Institute of Medical and Technical Sciences',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Thandalam',
          addressLocality: 'Chennai',
          addressRegion: 'Tamil Nadu',
          postalCode: '602105',
          addressCountry: 'IN',
        },
      },
      organizer: {
        '@type': 'CollegeOrUniversity',
        name: 'SIMATS Saveetha University',
        url: 'https://www.simats.ac.in',
      },
      performer: { '@id': `${SITE_URL}/#person` },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'INR',
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}/#india-visit`,
      },
    },
    {
      '@type': 'Event',
      name: 'Day 02 — Immersive AR & VR Hands-On Workshop at SIMATS',
      description: 'Hands-on VR headset experience, Unity Engine, Unreal Engine 5, Sketchfab 3D ecosystem, Mixed Reality spatial mapping, and .FBX asset pipelines.',
      startDate: '2026-02-12T09:00:00+05:30',
      endDate: '2026-02-12T17:00:00+05:30',
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      image: `${SITE_URL}/India/Day%202/766624925_943013838332152_8854407883256024945_n.jpg`,
      location: {
        '@type': 'Place',
        name: 'Center for Immersive Technologies, SIMATS',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Thandalam',
          addressLocality: 'Chennai',
          addressRegion: 'Tamil Nadu',
          postalCode: '602105',
          addressCountry: 'IN',
        },
      },
      organizer: {
        '@type': 'CollegeOrUniversity',
        name: 'SIMATS Saveetha University',
        url: 'https://www.simats.ac.in',
      },
      performer: { '@id': `${SITE_URL}/#person` },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'INR',
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}/#india-visit`,
      },
    },
  ],
};

// ── Individual India Post Article Schemas ────────────────────────────────────
const indiaArticleSchemas = [
  {
    '@type': 'BlogPosting',
    '@id': `${SITE_URL}/#india-post-schol-giving-day`,
    headline: 'Stepping Into My First International Experience — Marisilin Bijosilin (Bijon) at Northern UNI India Mobility Programme',
    alternativeHeadline: 'Marisilin Bijosilin Selected as Top 12 Students of Northern UNI for Fully Funded India Trip',
    description:
      'Marisilin Bijosilin (Bijon) shares the scholarship and visa ceremony experience after being selected among the Top 12 Performing Students of Northern UNI for the fully funded International Student Mobility Programme to SIMATS Saveetha University, Chennai, India.',
    author: { '@id': `${SITE_URL}/#person` },
    publisher: { '@id': `${SITE_URL}/#person` },
    mainEntityOfPage: `${SITE_URL}/#india-post-schol-giving-day`,
    datePublished: '2026-02-08',
    dateModified: '2026-08-12',
    inLanguage: 'en',
    about: ['Student Mobility', 'Scholarship Ceremony', 'International Education', 'Sri Lanka to India'],
    keywords: 'Marisilin Bijosilin, Bijon India trip, Northern UNI scholarship ceremony, top 12 students Northern UNI, Marisilin Bijosilin Synergy Consultant visa presentation, Gayan Wijeyasekara, Siveneshvari Miss, Sangeetha Raveenthirathas, SLIIT Northern campus India, fully funded student mobility Sri Lanka',
    image: `${SITE_URL}/India/Schol%20giving%20Day/762998284_2378259819585420_7330938254102123475_n.jpg`,
  },
  {
    '@type': 'BlogPosting',
    '@id': `${SITE_URL}/#india-post-airport-journey`,
    headline: 'From Jaffna to India — Marisilin Bijosilin\'s First International Flight Journey Begins',
    alternativeHeadline: 'Bijon and Northern UNI Students Depart from Jaffna International Airport to Chennai India',
    description:
      'Marisilin Bijosilin (Bijon) and the Northern UNI Top 12 student delegation depart from Jaffna International Airport for the 7-day international student mobility programme at SIMATS Saveetha University, Chennai.',
    author: { '@id': `${SITE_URL}/#person` },
    publisher: { '@id': `${SITE_URL}/#person` },
    mainEntityOfPage: `${SITE_URL}/#india-post-airport-journey`,
    datePublished: '2026-02-10',
    dateModified: '2026-08-12',
    inLanguage: 'en',
    about: ['International Travel', 'Student Exchange', 'Jaffna Airport', 'Chennai Airport'],
    keywords: 'Marisilin Bijosilin airport, Bijon Jaffna airport, Jaffna International Airport departure, Northern UNI students India flight, Jaffna to Chennai flight, student mobility programme departure, Sri Lanka India student travel',
    image: `${SITE_URL}/India/Pics%20at%20Airport/769175251_1737809011302028_7975668644962506746_n.jpg`,
  },
  {
    '@type': 'BlogPosting',
    '@id': `${SITE_URL}/#india-post-day-01`,
    headline: 'Day 01 — Marisilin Bijosilin\'s First Impressions at SIMATS Saveetha University Chennai',
    alternativeHeadline: 'Bijon\'s Campus Tour, SAIL Library, IoT Lab & AR/VR Preview at SIMATS Chennai',
    description:
      'Day 01 of the Northern UNI India Mobility Programme with Marisilin Bijosilin (Bijon): campus exploration at SIMATS Saveetha Institute of Medical and Technical Sciences, visiting the SAIL Library, IoT labs, AR/VR labs, 3D printing hub, and drone technology demonstrations.',
    author: { '@id': `${SITE_URL}/#person` },
    publisher: { '@id': `${SITE_URL}/#person` },
    mainEntityOfPage: `${SITE_URL}/#india-post-day-01`,
    datePublished: '2026-02-10',
    dateModified: '2026-08-12',
    inLanguage: 'en',
    about: ['SIMATS University', 'Campus Tour', 'SAIL Library', 'IoT', 'AR/VR', '3D Printing', 'Drone Technology'],
    keywords: 'Marisilin Bijosilin SIMATS, Bijon SIMATS campus tour, SAIL Library SIMATS, IoT lab SIMATS Chennai, AR VR lab Saveetha University, 3D printing SIMATS, drone technology demonstration Chennai, Northern UNI day 01 India',
    image: `${SITE_URL}/India/Day%2001/766890198_1703536287541961_8847500469522533914_n.jpg`,
  },
  {
    '@type': 'BlogPosting',
    '@id': `${SITE_URL}/#india-post-day-02`,
    headline: 'Day 02 — Marisilin Bijosilin Steps Into the World of AR & VR at SIMATS Chennai',
    alternativeHeadline: 'Bijon\'s Hands-On AR VR MR XR Workshop at Saveetha University — Unity, Unreal Engine, Sketchfab',
    description:
      'Marisilin Bijosilin (Bijon) explores Day 02 immersive technology workshop: hands-on Virtual Reality headsets, Unity Engine, Unreal Engine 5, Mixed Reality spatial mapping, Sketchfab 3D ecosystem, and .FBX asset pipelines at SIMATS Center for Immersive Technologies.',
    author: { '@id': `${SITE_URL}/#person` },
    publisher: { '@id': `${SITE_URL}/#person` },
    mainEntityOfPage: `${SITE_URL}/#india-post-day-02`,
    datePublished: '2026-02-11',
    dateModified: '2026-08-12',
    inLanguage: 'en',
    about: ['Virtual Reality', 'Augmented Reality', 'Mixed Reality', 'Unity Engine', 'Unreal Engine 5', 'Sketchfab', 'Immersive Technology'],
    keywords: 'Marisilin Bijosilin VR workshop, Bijon AR VR, AR VR workshop SIMATS, VR headset hands-on Chennai, Unity Engine workshop India, Unreal Engine 5 student workshop, Mixed Reality spatial mapping, Sketchfab 3D, immersive technology workshop SIMATS, XR workshop Saveetha University',
    image: `${SITE_URL}/India/Day%2002/772280521_1691501561944587_953751068733738689_n.jpg`,
  },
];

// ── India Image Gallery Schema ────────────────────────────────────────────────
const indiaImageGallerySchema = {
  '@type': 'ImageGallery',
  '@id': `${SITE_URL}/#india-photo-gallery`,
  name: 'Marisilin Bijosilin (Bijon) — Northern UNI India Mobility Programme 2026 Photo Gallery',
  description:
    'Complete photo journal documenting Marisilin Bijosilin\'s 7-day fully funded international student mobility programme from Northern UNI (SLIIT Northern Campus), Jaffna, Sri Lanka to SIMATS Saveetha University, Chennai, India. ' +
    'Featuring scholarship ceremony, airport departure, campus tour, AR/VR workshops, IoT demonstrations, 3D printing, and drone technology.',
  author: { '@id': `${SITE_URL}/#person` },
  about: { '@id': `${SITE_URL}/#india-mobility-programme-2026` },
  inLanguage: 'en',
  keywords: 'Marisilin Bijosilin India photos, Bijon India pictures, Northern UNI India photos, SIMATS campus photos, SLIIT Northern student mobility images, Jaffna India trip photos, Saveetha University student exchange gallery, Northern University India 2026 pictures',
};

const SCHEMA_GRAPH = {
  '@context': 'https://schema.org',
  '@graph': [
    personSchema,
    websiteSchema,
    webPageSchema,
    researchSchema,
    projectsSchema,
    certsSchema,
    workSchema,
    educationSchema,
    h2fShowcaseSchema,
    volunteeringSchema,
    speakingEventSchema,
    blogPostsSchema,
    youtubeSchema,
    faqSchema,
    breadcrumbSchema,
    indiaVisitSchema,
    indiaImageGallerySchema,
    ...indiaArticleSchemas,
    ...competitionSchemas,
    ...credentialSchemas,
    ...organizationSchemas,
    ...educationOrgSchemas,
  ],
};

// ─────────────────────────────────────────────────────────────────────────────

export default function SEO() {
  const TITLE = 'Marisilin Bijosilin (Bijon) | Full Stack Developer, ML Researcher';
  const DESC =
    'Marisilin Bijosilin (Bijon), First Class BSc (Hons) software engineer at WECONN3CT GmbH. MERN, Laravel, FastAPI and ECG arrhythmia AI research. Jaffna, Sri Lanka.';

  return (
    <Helmet>
      {/* ── Primary Meta Tags ── */}
      <html lang="en" />
      <title>{TITLE}</title>
      <meta name="title"       content={TITLE} />
      <meta name="description" content={DESC} />
      <meta name="author"      content="Marisilin Bijosilin" />
      <meta name="robots"      content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="theme-color" content="#0d0d0d" />
      <link rel="canonical"    href={SITE_URL} />
      <link rel="icon" type="image/png" href="/images/final.png" />
      <link rel="apple-touch-icon" href="/images/final.png" />

      {/* ── Open Graph / Facebook / LinkedIn ── */}
      <meta property="og:type"         content="profile" />
      <meta property="og:site_name"    content="Marisilin Bijosilin" />
      <meta property="og:url"          content={SITE_URL} />
      <meta property="og:title"        content={TITLE} />
      <meta property="og:description"  content="First Class BSc (Hons) software engineer at WECONN3CT GmbH. MERN, Laravel, FastAPI and medical AI research." />
      <meta property="og:image"        content={OG_IMAGE} />
      <meta property="og:image:alt"    content="Marisilin Bijosilin, full stack developer and ML researcher" />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale"       content="en_US" />
      <meta property="profile:first_name" content="Marisilin" />
      <meta property="profile:last_name"  content="Bijosilin" />

      {/* ── Twitter / X ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content="Marisilin Bijosilin (Bijon) | Full Stack Developer" />
      <meta name="twitter:description" content="First Class BSc (Hons) software engineer. MERN, Laravel, FastAPI, ECG AI research." />
      <meta name="twitter:image"       content={OG_IMAGE} />
      <meta name="twitter:image:alt"   content="Marisilin Bijosilin, full stack developer and ML researcher" />

      {/* ── JSON-LD structured data (One Connected Graph) ── */}
      <script type="application/ld+json">
        {JSON.stringify(SCHEMA_GRAPH)}
      </script>
    </Helmet>
  );
}
